import { Link } from "react-router-dom";
import NavigationBar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import PageHeader from "@/components/feature/PageHeader";
import SectionTitle from "@/components/base/SectionTitle";
import { useSeo } from "@/hooks/useSeo";
import { categories } from "@/mocks/categories";
import { siteInfo, themeOf } from "@/utils/content";

const principles = [
  {
    icon: "ri-focus-3-line",
    title: "只记录能被讲述的具体细节",
    text: "我们更关心「发生了什么、当时怎么处理」，而不是给谁下结论。没有具体场景的说法，宁可先不发。",
  },
  {
    icon: "ri-shield-user-line",
    title: "先做脱敏，再谈可读性",
    text: "真实姓名、可识别的联系方式、隐私信息一律处理后再呈现。可读性不能让位于对他人生活的打扰。",
  },
  {
    icon: "ri-scales-3-line",
    title: "把热闹拆成可以理解的逻辑",
    text: "同一件事，我们希望多写一层「为什么会这样」。让读者看完之后，不只知道结果，还多一点判断力。",
  },
  {
    icon: "ri-refresh-line",
    title: "内容会持续被修正",
    text: "如果发现事实有出入，我们会优先核查并更新说明，而不是悄悄改动。修正本身也是内容的一部分。",
  },
];

const voices = [
  {
    quote: "分类入口做得很直接，我基本只逛职场和同城，两下就点到了，不用在首页翻半天。",
    name: "在看板前发呆",
    role: "产品岗读者",
  },
  {
    quote: "喜欢那种把前因后果讲清楚的文章，看完之后不只是知道了个热闹，还学到点东西。",
    name: "晚九点下班",
    role: "校园板块常客",
  },
  {
    quote: "标签和目录很实用，长文也能跳着看。希望后面能按城市再细分一点。",
    name: "巷口便利店",
    role: "同城板块读者",
  },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 品牌介绍、内容原则与联系方式 | 511爆料",
    description:
      "511爆料是一个以分类导航为核心的爆料内容门户，覆盖娱乐圈瓜、职场瓜、校园瓜、同城瓜与行业瓜。这里介绍我们的定位、内容原则、读者反馈与联系方式。",
    keywords: "511爆料,关于511爆料,511爆料内容原则,511爆料分类导航,511爆料联系方式",
    canonical: "/about",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于 511爆料",
        url: `${siteInfo.baseUrl}/about`,
        inLanguage: "zh-CN",
        description: "511爆料的品牌定位、内容原则与联系方式。",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "511爆料",
        url: siteInfo.baseUrl,
        email: siteInfo.email,
        description: siteInfo.intro,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: siteInfo.baseUrl },
          { "@type": "ListItem", position: 2, name: "关于我们", item: `${siteInfo.baseUrl}/about` },
        ],
      },
    ],
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <NavigationBar />
      <main className="flex-1">
        <PageHeader
          eyebrow="关于我们"
          title="把热闹分类整理好，让你少走弯路"
          description="511爆料是一个以分类导航为核心的爆料内容门户。我们做的主要是两件事：把内容分好类，把细节讲清楚。"
          crumbs={[{ label: "关于我们" }]}
        />

        <section className="mx-auto max-w-6xl px-4 py-14 md:px-8 md:py-20">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-14">
            <div>
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
                <span className="inline-block h-px w-6 bg-primary-500" />
                品牌介绍
              </p>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">我们是谁</h2>
              <div className="mt-4 flex flex-col gap-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                <p>
                  511爆料从一个很小的想法开始：网上的信息很多，但真正难的从来不是「找得到」，而是「分得清」。
                  于是我们把内容收进五个清晰的方向——娱乐圈瓜、职场瓜、校园瓜、同城瓜与行业瓜。
                </p>
                <p>
                  我们不追求成为最快的那一个，而是希望成为「看完之后不那么迷茫」的那一个。
                  每条内容尽量交代清楚时间、场景和身份视角，让读者能自己判断，而不是被动接受一个结论。
                </p>
              </div>
              <dl className="mt-8 grid grid-cols-2 gap-6 sm:grid-cols-3">
                <div>
                  <dt className="text-xs text-foreground-500">内容板块</dt>
                  <dd className="mt-1 font-heading text-xl font-bold text-foreground-950">5 个</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500">内容总量</dt>
                  <dd className="mt-1 font-heading text-xl font-bold text-foreground-950">4,600+</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500">更新节奏</dt>
                  <dd className="mt-1 font-heading text-xl font-bold text-foreground-950">每日</dd>
                </div>
              </dl>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 h-56 w-full overflow-hidden rounded-lg md:h-64">
                <img
                  src="https://readdy.ai/api/search-image?query=Stylized%20flat%20illustration%20of%20an%20abstract%20editorial%20desk%20with%20sorting%20trays%20and%20warm%20lamp%20light%2C%20coral%20and%20olive%20palette%2C%20geometric%20minimal%20art%2C%20no%20people&width=1200&height=700&seq=about-511-01&orientation=landscape"
                  alt="511爆料编辑部内容分类整理的插画示意"
                  title="511爆料 - 品牌介绍"
                  className="h-full w-full object-top object-cover"
                />
              </div>
              <div className="h-40 w-full overflow-hidden rounded-lg md:h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Stylized%20flat%20illustration%20of%20abstract%20speech%20bubbles%20and%20bookmark%20shapes%20in%20warm%20cream%20and%20amber%20palette%2C%20geometric%20minimal%20art%2C%20no%20people&width=800&height=800&seq=about-511-02&orientation=squarish"
                  alt="511爆料读者互动的插画示意"
                  title="511爆料 - 读者互动"
                  className="h-full w-full object-top object-cover"
                />
              </div>
              <div className="h-40 w-full overflow-hidden rounded-lg md:h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=Stylized%20flat%20illustration%20of%20abstract%20compass%20and%20map%20route%20shapes%20in%20coral%20and%20cream%20palette%2C%20geometric%20minimal%20art%2C%20no%20people&width=800&height=800&seq=about-511-03&orientation=squarish"
                  alt="511爆料分类导航概念的插画示意"
                  title="511爆料 - 分类导航"
                  className="h-full w-full object-top object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="mx-auto max-w-6xl px-4 md:px-8">
            <SectionTitle
              eyebrow="内容原则"
              title="我们坚持的四件事"
              description="热闹可以很好看，但内容需要一条底线。下面四条，是我们判断一条内容该不该发、该怎么发的依据。"
            />
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {principles.map((item) => (
                <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary-500 text-xl text-background-50">
                    <i className={item.icon} />
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="mx-auto max-w-6xl px-4 md:px-8">
            <SectionTitle
              eyebrow="分类一览"
              title="五个方向，各有各的读法"
              description="每个板块的内容气质不太一样，进入之后你会很快感受到差别。"
            />
            <div className="mt-8 flex flex-wrap gap-3">
              {categories.map((item) => {
                const theme = themeOf(item.slug);
                return (
                  <Link
                    key={item.slug}
                    to={`/category/${item.slug}`}
                    className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors ${theme.border} ${theme.soft} ${theme.text}`}
                  >
                    <i className={item.icon} />
                    {item.name}
                    <span className="text-xs opacity-70">{item.count}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        <section className="w-full bg-secondary-50 py-14 md:py-20">
          <div className="mx-auto max-w-6xl px-4 md:px-8">
            <SectionTitle
              eyebrow="读者留言"
              title="他们是这样用 511爆料的"
              description="下面几条来自读者反馈，我们保留了他们说话的原样。"
            />
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
              {voices.map((item) => (
                <figure key={item.name} className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-6">
                  <i className="ri-double-quotes-l text-2xl text-accent-500" />
                  <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{item.quote}</blockquote>
                  <figcaption className="mt-4 border-t border-background-200 pt-4">
                    <span className="block text-sm font-semibold text-foreground-950">{item.name}</span>
                    <span className="mt-0.5 block text-xs text-foreground-500">{item.role}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="mx-auto max-w-6xl px-4 md:px-8">
            <div className="grid grid-cols-1 gap-8 rounded-lg border border-background-200 bg-background-100 p-8 md:p-10 lg:grid-cols-2">
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-950">联系我们</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  无论是内容反馈、投稿建议，还是合作咨询，都可以通过下面的邮箱找到我们。我们会尽量在三个工作日内回复。
                </p>
                <a
                  href={`mailto:${siteInfo.email}`}
                  className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-mail-send-line" />
                  {siteInfo.email}
                </a>
              </div>
              <dl className="flex flex-col gap-5">
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wider text-foreground-500">联系邮箱</dt>
                  <dd className="mt-1 text-sm text-foreground-800">{siteInfo.email}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wider text-foreground-500">内容范围</dt>
                  <dd className="mt-1 text-sm text-foreground-800">娱乐圈瓜 · 职场瓜 · 校园瓜 · 同城瓜 · 行业瓜</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wider text-foreground-500">投稿建议</dt>
                  <dd className="mt-1 text-sm leading-relaxed text-foreground-800">
                    写清时间、场景与身份视角；涉及他人时请先做脱敏，不要包含真实姓名或联系方式。
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}